import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { PermissionGate } from "../PermissionGate";
import { useSignedInWithOptro } from "../../hooks/useSignedInWithOptro";
import {
  FeatureFlag,
  useFeatureFlag,
} from "../../contexts/launchDarklyContext";
import { useOptroSend } from "../../contexts/optroSendContext";
import {
  formatSendTime,
  SIGN_IN_REQUIRED_COPY,
} from "../../lib/optroSendMessages";
import { useAuth } from "../../contexts/authContext";
import { hasPermission, runTestPermissionFor } from "../../lib/permissions";
import { ControlSessionEnvelope } from "../../models/audit/controlSession";
import type { SessionKind } from "../../hooks/audit/useControlSessionEnvelope";

interface SendToOptroButtonProps {
  controlSession: ControlSessionEnvelope;
  sessionKind: SessionKind;
  // The template the export tab currently shows; the send renders exactly it.
  selectedTemplateId: string;
  // Field values are being generated; a send now would carry the old ones.
  generating: boolean;
}

// Why the button can't be used right now, or when the session was last sent.
// Null for no tooltip.
function sendTooltip(
  signedInWithOptro: boolean,
  sending: boolean,
  generating: boolean,
  lastSentLabel: string | null,
): string | null {
  if (!signedInWithOptro) {
    return SIGN_IN_REQUIRED_COPY;
  }
  if (sending) {
    return null;
  }
  if (generating) {
    return "Wait for the field values to finish generating.";
  }
  if (lastSentLabel) {
    return `Last sent ${lastSentLabel}`;
  }
  return null;
}

function sendLabel(sending: boolean, sentBefore: boolean): string {
  if (sending) {
    return "Sending to Optro...";
  }
  if (sentBefore) {
    return "Resend to Optro";
  }
  return "Send to Optro";
}

// Rendered for a session linked to Optro while the kind's LaunchDarkly flag
// is on; PermissionGate keeps it visible but inert without run-test.
export function SendToOptroButton({
  controlSession,
  sessionKind,
  selectedTemplateId,
  generating,
}: SendToOptroButtonProps) {
  const flagOn = useFeatureFlag(
    sessionKind === "work_step"
      ? FeatureFlag.workStepsIntegration
      : FeatureFlag.optroMidshipIntegration,
  );
  const { sendingSessionIds, send } = useOptroSend();
  const { workspaceMember } = useAuth();
  // PermissionGate only blocks the pointer, so the button itself is disabled
  // for a keyboard user without the permission too.
  const canSend = hasPermission(
    workspaceMember,
    runTestPermissionFor(sessionKind),
  );
  // The send runs as the user's Optro sign-in, so a session signed in any
  // other way cannot send.
  const signedInWithOptro = useSignedInWithOptro();

  // Like Download Workpaper, a send needs neither a complete session nor
  // generated values: it goes out as the session stands.
  if (!controlSession.optroLink || !flagOn) {
    return null;
  }

  const sessionId = controlSession.id;
  const entityName =
    (sessionKind === "work_step"
      ? controlSession.workStep?.name
      : controlSession.control?.name) ?? null;
  const sending = sendingSessionIds.has(sessionId);
  const disabled = !canSend || !signedInWithOptro || sending || generating;
  // The envelope is refetched once a send finishes, so this is always the
  // backend's own record of the last send.
  const { optroLastSentAt } = controlSession;
  const lastSentLabel = optroLastSentAt
    ? formatSendTime(optroLastSentAt)
    : null;
  const tooltip = sendTooltip(
    signedInWithOptro,
    sending,
    generating,
    lastSentLabel,
  );

  // One tree whatever the state: swapping the wrapper when `sending` flips
  // would remount the button and drop its keyboard focus.
  return (
    <PermissionGate permission={runTestPermissionFor(sessionKind)}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span: disabled buttons swallow pointer events and focus, so the
              trigger needs an enabled wrapper that keyboard users can reach
              while the button cannot take focus */}
          <span
            tabIndex={disabled && (tooltip || !canSend) ? 0 : undefined}
            className="inline-flex"
          >
            <Button
              variant="outline"
              disabled={disabled}
              data-testid="send-to-optro-button"
              onClick={() => {
                void send(sessionId, selectedTemplateId, entityName);
              }}
            >
              {sendLabel(sending, optroLastSentAt !== null)}
            </Button>
          </span>
        </TooltipTrigger>
        {tooltip && (
          <TooltipContent data-testid="send-to-optro-tooltip">
            {tooltip}
          </TooltipContent>
        )}
      </Tooltip>
    </PermissionGate>
  );
}
